import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import SelectionView, { SelectionInvalid } from '@/components/SelectionView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

/**
 * Ops-only preview of the client selection page. Reuses the same visual
 * component with previewMode enabled. Reads via /api/ops/leads/{id}/selection-preview
 * which does not consume or invalidate the real token.
 */
export default function ClientSelectionPreview() {
  const { leadId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!authLoading && (!user || !(user.roles || [user.role]).some(r => ['admin', 'interviewer'].includes(r)))) {
      navigate('/ops/login');
    }
  }, [user, authLoading, navigate]);

  const fetchPreview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ops/leads/${leadId}/selection-preview`, getAuth());
      setData(data);
      if (data?.selected_items?.length) setSelected(data.selected_items);
    } catch (e) {
      setError(e.response?.status === 404 ? 'invalid' : 'error');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (user && (user.roles || [user.role]).some(r => ['admin', 'interviewer'].includes(r))) fetchPreview();
  }, [user, fetchPreview]);

  const toggle = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      const optional = prev.filter(pid => {
        const k = data.keepsakes.find(kk => kk.id === pid);
        return k && !k.is_always_included;
      });
      if (optional.length >= 4) return prev;
      return [...prev, id];
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#A89F91] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error === 'invalid' || !data) return <SelectionInvalid />;

  return (
    <SelectionView
      data={data}
      selected={selected}
      onToggle={toggle}
      onSave={null}
      saving={false}
      savedAt={null}
      error={null}
      previewMode={true}
    />
  );
}
